import { Directive, ElementRef, Input, Renderer2, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { CommonService } from '../services/common.service';
import { take } from 'rxjs/operators';
import { ToastTimerConfig } from '../config/toast-timer-config'; // ✅ Import ToastTimerConfig

@Directive({
  selector: '[appToastBadge]',
})
export class ToastBadgeDirective implements OnInit, OnDestroy {
  @Input() toastType: 'success' | 'error' | 'warn' | 'info' = 'info'; // Default type
  private toastSubscription!: Subscription;
  private intervalId!: any; // ✅ Stores interval ID to clear later

  blinkDuration = ToastTimerConfig.BLINKING_TOAST_DURATION; // ✅ Use config for total blink duration
  blinkInterval = ToastTimerConfig.BLINK_INTERVAL; // ✅ Use config for blink interval

  constructor(private el: ElementRef, private renderer: Renderer2, private commonService: CommonService) {}

  ngOnInit() {
    // ✅ Subscribe to toast events but only take one instance (prevents memory leaks)
    this.toastSubscription = this.commonService.getToastObservable()
      .pipe(take(1))
      .subscribe((toastData: any) => {
        if (toastData && toastData.elementId === this.el.nativeElement.id) {
          this.triggerToastBadge(toastData);
        }
      });
  }

  /**
   * Triggers blinking border and displays toast badge inside the component.
   */
  private triggerToastBadge(toastData: any) {
    const { type, title, message } = toastData;
    const borderColor = this.getToastColor(type);

    // ✅ Apply Blinking Effect for Configured Duration
    let blinkCount = 0;
    const blinkCountMax = this.blinkDuration / this.blinkInterval; // ✅ Total blinks

    this.renderer.setStyle(this.el.nativeElement, 'border', `2px solid ${borderColor}`);

    this.intervalId = setInterval(() => {
      blinkCount++;
      this.renderer.setStyle(this.el.nativeElement, 'border-color', blinkCount % 2 === 0 ? 'transparent' : borderColor);

      if (blinkCount >= blinkCountMax) { // ✅ Stops blinking after configured duration
        clearInterval(this.intervalId);
        this.renderer.removeStyle(this.el.nativeElement, 'border');
      }
    }, this.blinkInterval);

    // ✅ Create Toast Badge
    const toastBadge = this.renderer.createElement('div');
    this.renderer.addClass(toastBadge, 'toast-badge');
    this.renderer.setStyle(toastBadge, 'background', borderColor);
    this.renderer.setProperty(toastBadge, 'innerHTML', `<strong>${title}:</strong> ${message}`);

    // ✅ Position Badge at Top
    this.renderer.setStyle(toastBadge, 'position', 'absolute');
    this.renderer.setStyle(toastBadge, 'top', '-10px');
    this.renderer.setStyle(toastBadge, 'left', '50%');
    this.renderer.setStyle(toastBadge, 'transform', 'translateX(-50%)');
    this.renderer.setStyle(toastBadge, 'padding', '5px 10px');
    this.renderer.setStyle(toastBadge, 'border-radius', '5px');
    this.renderer.setStyle(toastBadge, 'font-size', '12px');
    this.renderer.setStyle(toastBadge, 'color', 'white');
    this.renderer.setStyle(toastBadge, 'white-space', 'nowrap');
    this.renderer.setStyle(toastBadge, 'z-index', '1000');
    this.renderer.setStyle(toastBadge, 'box-shadow', '0px 4px 10px rgba(0, 0, 0, 0.1)');

    // ✅ Attach the badge
    this.renderer.appendChild(this.el.nativeElement, toastBadge);

    // ✅ Auto-remove badge after configured duration
    setTimeout(() => {
      this.renderer.removeChild(this.el.nativeElement, toastBadge);
    }, ToastTimerConfig.BLINKING_TOAST_DURATION);
  }

  /**
   * Returns the correct color based on toast type.
   */
  private getToastColor(type: string): string {
    const colorMap: { [key: string]: string } = {
      success: '#22c55e', // Green
      info: '#6366f1', // Purple
      warn: '#facc15', // Yellow
      error: '#fd0000', // Red
    };
    return colorMap[type] || '#000000'; // Default Black
  }

  ngOnDestroy() {
    if (this.toastSubscription) {
      this.toastSubscription.unsubscribe();
    }
    if (this.intervalId) {
      clearInterval(this.intervalId); // ✅ Prevents memory leaks
    }
  }
}
